/*!
 * scatter.js — scatter(): throw what is drawn about, and back
 * © 2026 Rob Canning — GPLv3
 *
 *     sh > scatter(0.4)              sh's paths thrown about (0..1 of its size)
 *     sh > scatter(0.4, dur:2)       ... over 2 beats
 *     scatter(0.3)                   with no target: the whole stage
 *     sh > scatter(0)                back where they were
 *     sh > scatter(follow(bass.level, 0, 0.6))   as far as a signal says
 *
 * Each path (polygon, line, text ...) goes its own random way and turns; a
 * scatter run again throws them anew, a followed one keeps its directions
 * and only goes further or nearer. A sound's box scatters what it draws. A
 * shape goes whole (each of a cluster's, its own way), so that a rotate()
 * on it still turns it about its own centre where it lands. What would be
 * thrown off the screen comes back off its edge: any amount stays in sight.
 *
 * Cheap: one CSS translate / rotate per element -- the individual transform
 * properties, which leave an element's own transform (its place, its
 * turning) as it was.
 */

import { isMoving, bindParam, releaseBinding } from "../control/paramBinding.js";
import { waveformsForElement } from "../system/waveform.js";
import { levelArgs } from "./audio/level.js";
import { secs } from "./beats.js";

// foreignObject: a scope draws on a canvas inside one -- that is what is seen
const GEOM = "path, polygon, polyline, line, circle, ellipse, rect, text, use, image, foreignObject";
const thrown = new Map();          // root -> [{ el, dx, dy, deg }] (unit directions)

/** What a scatter moves: the target's drawn pieces, or the stage's. */
function pieces(root) {
  const out = [];
  if (root?.dataset?.liveAudio != null) {
    // a sound's box: what it draws, not the box
    for (const h of waveformsForElement(root)) if (h.group) out.push(...h.group.querySelectorAll(GEOM));
    return out;
  }
  if (root.matches?.(GEOM) && !root.querySelector?.(GEOM)) return [root];
  const found = [...root.querySelectorAll(GEOM)].filter(e => e.id !== "oscilla-stage-hit" && !e.closest("defs, clipPath, mask"));
  // a shape's body sits inside its animations' wrappers (shape > anim >
  // anim-rotate > path, the turning about a pivot fixed in px): moved in
  // there it would swing round where it was. The shape goes, whole.
  return [...new Set(found.map(e => e.closest(".oscilla-shape") || e))];
}

/** x folded back into lo..hi off its edges, as a ball off a wall. */
function bounce(x, lo, hi) {
  if (!(hi > lo)) return (lo + hi) / 2;
  const span = hi - lo, t = (((x - lo) % (2 * span)) + 2 * span) % (2 * span);
  return lo + (t > span ? 2 * span - t : t);
}

/**
 * Where a piece is when not thrown, in screen px, and its half size --
 * measured with what it is thrown now (its translate, mid-way in a
 * transition too) taken off -- and its parent's matrix, to go between
 * screen px and the units its translate is in.
 */
function restOf(el) {
  const m = el.parentNode?.getScreenCTM?.();
  const r = el.getBoundingClientRect();
  const [tx = 0, ty = 0] = (getComputedStyle(el).translate || "").split(/\s+/).map(parseFloat).map(v => (Number.isFinite(v) ? v : 0));
  const k = m || { a: 1, b: 0, c: 0, d: 1 };
  return {
    m: k,
    x: (r.left + r.right) / 2 - (k.a * tx + k.c * ty),
    y: (r.top + r.bottom) / 2 - (k.b * tx + k.d * ty),
    hw: r.width / 2, hh: r.height / 2,
  };
}

/** Throw them `amount` (0..1 of the root's size) over `dur` s: new directions unless `keep`. */
function throwAbout(root, amount, dur = 0.6, keep = false) {
  for (const r of [...thrown.keys()]) if (!r.isConnected) thrown.delete(r);   // a shape since removed
  let set = thrown.get(root);
  if (!set || !keep) {
    set = pieces(root).map(el => {
      const a = Math.random() * 2 * Math.PI, r = 0.4 + Math.random() * 0.6;
      return { el, dx: Math.cos(a) * r, dy: Math.sin(a) * r, deg: (Math.random() * 2 - 1) * 180, ownTransform: el.hasAttribute("transform"), transition: el.style.transition, rest: restOf(el) };
    });
    thrown.set(root, set);
  }
  // how far: a fraction of the target's size on screen, put into each
  // piece's own units (a shape is drawn scaled, the stage in score units)
  // (no more than the screen: the stage's SVG is the whole score, mostly off it)
  const box = root.getBoundingClientRect();
  const reach = Math.min(Math.max(box.width, box.height) || 200, Math.max(window.innerWidth, window.innerHeight));
  const a = Math.max(0, Number(amount) || 0);
  for (const p of set) {
    const s = p.el.style;
    // translate / rotate go outside an element's own transform: its parent's units
    const { m, x, y, hw, hh } = p.rest;
    const unit = Math.hypot(m.a, m.b) || 1;
    // where it would go, in screen px, kept on the screen (off its edges)
    const sx = p.dx * a * reach, sy = p.dy * a * reach;
    const vx = bounce(x + sx, hw, innerWidth - hw) - x;
    const vy = bounce(y + sy, hh, innerHeight - hh) - y;
    // back into the parent's units (the inverse of its matrix's turn and scale)
    const det = (m.a * m.d - m.b * m.c) || unit * unit;
    const ux = (m.d * vx - m.c * vy) / det, uy = (-m.b * vx + m.a * vy) / det;
    // its own transition (a shape's fade) kept beside the throw's
    const own = p.transition && p.transition !== "none" ? `${p.transition}, ` : "";
    s.transition = dur > 0 ? `${own}translate ${dur}s ease-in-out, rotate ${dur}s ease-in-out` : (own.slice(0, -2) || "none");
    // turning about its own centre -- not for one that has a transform of
    // its own, whose origin that would move
    if (!p.ownTransform) { s.transformBox = "fill-box"; s.transformOrigin = "center"; }
    s.translate = a ? `${ux.toFixed(2)}px ${uy.toFixed(2)}px` : "";
    s.rotate = a && !p.ownTransform ? `${(p.deg * a).toFixed(1)}deg` : "";
  }
  if (!a) thrown.delete(root);
}

/**
 * What a scatter threw of `el` (a shape, a cluster, a member of one) back
 * where it was, at once, and the scatter forgotten: a shape's line run again
 * says where it is, so it comes back (shape.js). A followed scatter on it lets go.
 */
export function clearScatter(el) {
  if (!el) return;
  for (const [root, set] of thrown) {
    if (root !== el && !el.contains?.(root) && !root.contains?.(el)) continue;
    for (const p of set) {
      const s = p.el.style;
      s.transition = "none"; s.translate = ""; s.rotate = "";
      requestAnimationFrame(() => { s.transition = p.transition || ""; });   // its own (a shape's fade) again
    }
    releaseBinding(`scatter:${root.id || "stage"}`);
    thrown.delete(root);
  }
}

/** scatter(amount, dur:) as a cue, on its target or the stage. */
export function handleScatterCue(ast, cueElement = null) {
  const root = cueElement || document.getElementById("score") || document.querySelector("#scoreContainer svg");
  if (!root) return;
  const args = ast?.args || [];
  const a = levelArgs(args);
  const raw = args.find(x => x.type === "values" || x.type === "amount")?.value ?? a.numbers[0] ?? 0.3;
  const dur = Number(secs(a.dur ?? 0.6));      // beats (beats.js)
  const owner = `scatter:${root.id || "stage"}`;
  if (isMoving(raw)) {
    // a followed amount: the same directions, further or nearer
    throwAbout(root, 0, 0, false);
    let first = true;
    bindParam(raw, v => { throwAbout(root, v, first ? dur : 0.05, !first); first = false; }, { owner, min: 0, max: 1, default: 0 });
  } else {
    releaseBinding(owner);
    throwAbout(root, raw, dur, false);
  }
}
